import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { EMPRESA, getEstado } from "./config";

// Paleta (azul / gris oscuro, colores sutiles).
const NAVY = [31, 41, 55]; // gris oscuro
const BLUE = [37, 99, 235]; // azul acento
const SLATE = [71, 85, 105];
const MUTED = [148, 163, 184];
const LIGHT = [241, 245, 249];
const BORDER = [226, 232, 240];

const fmtDate = (iso, withTime = true) => {
  if (!iso) return "—";
  try {
    const dt = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    const opts = withTime
      ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
      : { day: "2-digit", month: "2-digit", year: "numeric" };
    return dt.toLocaleString("es-ES", opts);
  } catch {
    return iso;
  }
};

const fullName = (p) => `${p?.nombre || ""} ${p?.apellidos || ""}`.trim() || "—";

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function generateAlbaranPDF(a) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 40;
  const estado = getEstado(a.estado);

  // ---------- HEADER ----------
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PW, 92, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(EMPRESA.nombre || "ALBARÁN DE RECOGIDA Y ENTREGA", M, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(203, 213, 225);
  doc.text("ALBARÁN DE RECOGIDA Y ENTREGA", M, 62);
  doc.setFontSize(8.5);
  doc.text(`${EMPRESA.persona}  ·  DNI/NIF: ${EMPRESA.dni}  ·  ${EMPRESA.direccion}`, M, 78);

  // Número + fecha (caja a la derecha)
  const boxW = 180;
  const boxX = PW - M - boxW;
  doc.setFillColor(...BLUE);
  doc.roundedRect(boxX, 22, boxW, 50, 5, 5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("ALBARÁN Nº", boxX + 12, 38);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(a.numero || "BORRADOR", boxX + 12, 55);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`Fecha: ${fmtDate(a.createdAt)}`, boxX + 12, 67);

  let y = 116;

  // Estado
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE);
  doc.text("ESTADO:", M, y);
  const est = estado.rgb;
  const label = estado.label.toUpperCase();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const lw = doc.getTextWidth(label) + 16;
  doc.setFillColor(est[0], est[1], est[2]);
  doc.roundedRect(M + 48, y - 9, lw, 14, 7, 7, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(label, M + 48 + 8, y);

  y += 22;

  // ---------- REMITENTE / DESTINATARIO (dos columnas) ----------
  const colGap = 16;
  const colW = (PW - 2 * M - colGap) / 2;
  const boxH = 132;

  const partyBox = (x, title, accent, p) => {
    doc.setFillColor(...LIGHT);
    doc.roundedRect(x, y, colW, boxH, 5, 5, "F");
    // barra de título
    doc.setFillColor(accent[0], accent[1], accent[2]);
    doc.roundedRect(x, y, colW, 22, 5, 5, "F");
    doc.rect(x, y + 12, colW, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.text(title, x + 12, y + 15);

    let yy = y + 38;
    const line = (label, value, bold = false) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...MUTED);
      doc.text(label.toUpperCase(), x + 12, yy);
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...NAVY);
      const txt = doc.splitTextToSize(value || "—", colW - 24);
      doc.text(txt, x + 12, yy + 11);
      yy += 11 + txt.length * 10 + 4;
    };
    line("Nombre", fullName(p), true);
    line("DNI / Móvil", `${p?.dni || "—"}    ·    ${p?.movil || "—"}`);
    line("Dirección", p?.direccion || "—");
  };

  partyBox(M, "RECOGIDA · REMITENTE", BLUE, a.remitente);
  partyBox(M + colW + colGap, "ENTREGA · DESTINATARIO", [124, 58, 237], a.destinatario);

  y += boxH + 18;

  // ---------- EMPRESA EMISORA (tabla) ----------
  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [["DATOS DE LA EMPRESA EMISORA", ""]],
    body: [
      ["Empresa", EMPRESA.nombre],
      ["Titular", EMPRESA.persona],
      ["DNI / NIF", EMPRESA.dni],
      ["Dirección", EMPRESA.direccion],
    ],
    theme: "grid",
    headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9, halign: "left" },
    bodyStyles: { fontSize: 9, textColor: SLATE },
    columnStyles: { 0: { cellWidth: 120, fontStyle: "bold", textColor: NAVY }, 1: { cellWidth: "auto" } },
    styles: { lineColor: BORDER, lineWidth: 0.5, cellPadding: 5 },
  });

  y = doc.lastAutoTable.finalY + 16;

  // ---------- OBSERVACIONES ----------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text("OBSERVACIONES / NOTAS", M, y);
  y += 6;
  doc.setDrawColor(...BORDER);
  doc.setFillColor(252, 252, 253);
  const obsLines = doc.splitTextToSize(a.observaciones || "—", PW - 2 * M - 20);
  const obsH = Math.max(40, obsLines.length * 11 + 16);
  doc.roundedRect(M, y, PW - 2 * M, obsH, 4, 4, "FD");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...SLATE);
  doc.text(obsLines, M + 10, y + 16);
  y += obsH + 24;

  // ---------- FIRMAS ----------
  const sigW = (PW - 2 * M - colGap) / 2;
  const sigH = 96;
  if (y + sigH > PH - 70) {
    doc.addPage();
    y = 60;
  }
  const imgRecogida = await loadImage(a.firmaRecogida);
  const imgEntrega = await loadImage(a.firmaEntrega);

  const sigBox = (x, title, img) => {
    doc.setDrawColor(...BORDER);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, y, sigW, sigH, 4, 4, "S");
    if (img) {
      const maxW = sigW - 24;
      const ratio = img.height / img.width || 0.4;
      const w = maxW;
      const h = Math.min(maxW * ratio, sigH - 34);
      try {
        doc.addImage(img, "PNG", x + 12, y + 8, w, h);
      } catch {
        /* ignore */
      }
    }
    doc.setDrawColor(...MUTED);
    doc.line(x + 12, y + sigH - 22, x + sigW - 12, y + sigH - 22);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...NAVY);
    doc.text(title, x + 12, y + sigH - 8);
  };
  sigBox(M, "Firma de Recogida", imgRecogida);
  sigBox(M + sigW + colGap, "Firma de Entrega", imgEntrega);

  // ---------- PIE DE PÁGINA (número de página) ----------
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(...BORDER);
    doc.line(M, PH - 38, PW - M, PH - 38);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(EMPRESA.nombre, M, PH - 24);
    doc.text(`Página ${i} de ${pageCount}`, PW - M, PH - 24, { align: "right" });
  }

  return doc;
}

export async function downloadAlbaranPDF(a) {
  const doc = await generateAlbaranPDF(a);
  doc.save(`Albaran_${a.numero || "borrador"}.pdf`);
}

export async function openAlbaranPDF(a) {
  const doc = await generateAlbaranPDF(a);
  const url = doc.output("bloburl");
  const win = window.open(url, "_blank", "noopener");
  if (!win) doc.save(`Albaran_${a.numero || "borrador"}.pdf`);
}
